import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { map, switchMap, catchError, mergeMap, take, tap } from 'rxjs/operators';
import { selectCapturesCurrentSessionId } from './captures.selectors';
import { CAPTURES_SERVICE } from '../../services/captures/captures.service.base';
import { ICapturesService } from '../../interfaces/services/captures-service.interface';
import { getErrorMessage } from '../../utils/error.utils';
import { FILE_UPLOAD_FAILED_MESSAGE, isUploadFailedStatus } from '../../utils/upload-retry.utils';
import { ToastService } from '../../services/toast.service';
import * as CapturesActions from './captures.actions';

@Injectable()
export class CapturesEffects {
  private actions$ = inject(Actions);
  private service = inject<ICapturesService>(CAPTURES_SERVICE);
  private toastService = inject(ToastService);
  private store = inject(Store);
  private currentSessionId$ = this.store.select(selectCapturesCurrentSessionId).pipe(take(1));

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapturesActions.loadCaptures),
      switchMap(({ sessionId }) =>
        this.service.getCaptures(sessionId).pipe(
          map((captures) => CapturesActions.loadCapturesSuccess({ captures })),
          catchError((error) => of(CapturesActions.loadCapturesFailure({ error: getErrorMessage(error) })))
        )
      )
    )
  );

  add$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapturesActions.addCapture),
      mergeMap(({ sessionId, name, file, floorId, position }) =>
        this.service.addCapture(sessionId, name, file, position).pipe(
          switchMap((capture) =>
            this.service.getCaptures(sessionId).pipe(
              mergeMap((captures) =>
                this.currentSessionId$.pipe(map((currentSessionId) => ({ captures, currentSessionId })))
              ),
              mergeMap(({ captures, currentSessionId }) => {
                const persisted = captures.find((c) => c.id === capture.id);
                // The upload runs in the background; only refresh the list if the user is still on this session.
                const refresh =
                  currentSessionId === sessionId ? [CapturesActions.loadCapturesSuccess({ captures })] : [];
                if (persisted && isUploadFailedStatus(persisted.status)) {
                  return from([
                    ...refresh,
                    CapturesActions.addCaptureFailure({
                      error: FILE_UPLOAD_FAILED_MESSAGE,
                    }),
                  ]);
                }
                return from([
                  ...refresh,
                  CapturesActions.addCaptureSuccess({ capture: persisted ?? capture, floorId }),
                ]);
              })
            )
          ),
          catchError((error) =>
            this.currentSessionId$.pipe(
              mergeMap((currentSessionId) =>
                from([
                  CapturesActions.addCaptureFailure({
                    error: getErrorMessage(error),
                  }),
                  ...(currentSessionId === sessionId ? [CapturesActions.loadCaptures({ sessionId })] : []),
                ])
              )
            )
          )
        )
      )
    )
  );

  addCaptureSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CapturesActions.addCaptureSuccess),
        tap(() => {
          this.toastService.success('Capture added successfully.', 'Success');
        })
      ),
    { dispatch: false }
  );

  addCaptureFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CapturesActions.addCaptureFailure),
        tap(({ error }) => {
          this.toastService.error(error, 'Error');
        })
      ),
    { dispatch: false }
  );

  retryProcessingCapture$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CapturesActions.retryProcessingCapture),
      mergeMap(({ sessionId, captureId, fileSize }) =>
        this.service.retryProcessingCapture(captureId, fileSize).pipe(
          mergeMap(() =>
            from([
              CapturesActions.retryProcessingCaptureSuccess(),
              CapturesActions.loadCaptures({ sessionId }),
            ])
          ),
          catchError((error) =>
            from([
              CapturesActions.retryProcessingCaptureFailure({ error: getErrorMessage(error) }),
              CapturesActions.loadCaptures({ sessionId }),
            ])
          )
        )
      )
    )
  );

  retryProcessingCaptureSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CapturesActions.retryProcessingCaptureSuccess),
        tap(() => {
          this.toastService.success('Capture processing retried successfully.', 'Success');
        })
      ),
    { dispatch: false }
  );

  retryProcessingCaptureFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CapturesActions.retryProcessingCaptureFailure),
        tap(({ error }) => {
          this.toastService.error(error, 'Error');
        })
      ),
    { dispatch: false }
  );
}
